<template>
  <div class="prism">
    <div
      class="h-full relative group flex flex-col gap-8 items-center"
      :class="{ 'w-full': fullWidth, 'max-w-md': !fullWidth }"
    >
      <div class="max-w-full" v-if="backgroundSuggestions.length > 0">
        <div class="flex flex-col gap-2 sm:flex-row justify-center items-center">
          <div class="w-full flex gap-3 flex-wrap playful-rotate" id="gallery">
            <ImageButton
              v-for="entry of backgroundSuggestions"
              :selected="isSelected(entry)"
              @click.prevent="isSelected(entry) ? deselectSuggestion(entry) : selectSuggestion(entry)"
              class="justify-center items-center"
            >
              <Image v-if="isSuggestionImage(entry)" :src="entry.thumbnail_url" alt="Example background image"></Image>
              <div v-else class="w-100 h-100" :style="`background-color:${entry};`"></div>
              <template #action>
                <MinusIcon />
              </template>
            </ImageButton>
            <!-- Uploaded Background tile -->
          </div>
        </div>
      </div>
      <div class="flex gap-3 justify-center items-center" v-if="showBlurToggle || showShadowToggle">
        <Toggle
          v-if="showBlurToggle"
          :name="I18n.t('editor.blur_background')"
          :label="I18n.t('editor.blur_background')"
          :model-value="applyBlur"
          @update:modelValue="toggleBlur"
        />
        <Toggle
          v-if="showShadowToggle"
          :name="I18n.t('editor.apply_shadow')"
          :label="I18n.t('editor.apply_shadow')"
          :model-value="applyShadow"
          @update:modelValue="toggleShadow"
        />
      </div>
      <div
        id="background-widget"
        ref="widgetElement"
        v-if="props.displayWidget"
        class="w-full grow justify-stretch sm:justify-center items-center sm:gap-8 rounded-4xl bg-white shadow-2xl bg-cover"
        :class="{ 'centered-upload-button': props.centeredUploadButton }"
        :style="widgetElementStyles"
      >
        <div class="w-full light-glass-background rounded-4xl button-padding">
          <Button class="w-full" size="lg" @click="uploadFile">{{ I18n.t("upload_widget.upload_image") }}</Button>
          <div class="hidden sm:flex flex-col gap-1.5 rounded-4xl px-4 py-2 white-shadow items-center">
            <p class="m-0 font-bold text-xl text-typo-secondary">{{ I18n.t("upload_widget.or_drop_a_file") }},</p>
            <span class="text-xs text-typo-secondary text-center">
              {{ I18n.t("upload_widget.paste_text_start").toLowerCase() }}
              <a
                href="#"
                class="text-typo-secondary select-photo-url-btn underline"
                @click.prevent="uploadUrl(event)"
                >{{ I18n.t("upload_widget.paste_text_url") }}</a
              >
              {{ I18n.t("upload_widget.paste_text_end") }}
            </span>
          </div>
        </div>
      </div>

      <div class="max-w-md">
        <div class="flex flex-col gap-2 sm:flex-row justify-between items-center">
          <div class="flex flex-row sm:!flex-col sm:justify-center">
            <span class="font-bold text-typo-secondary !mr-1 sm:!mr-0">{{ I18n.t("upload_widget.no_image") }}</span>
            <span class="font-bold text-typo-secondary">{{ I18n.t("upload_widget.try_one_of_these") }}</span>
          </div>
          <div class="flex gap-2">
            <ImageButton
              v-for="image in exampleImages"
              @click.prevent="exampleSelected(image)"
              :key="image.thumbnail_url"
              ondragstart="return false;"
            >
              <Image :src="image.thumbnail_url" alt="Example foreground image"></Image>
            </ImageButton>
          </div>
        </div>
        <p v-html="uploadTerms" class="text-2xs text-typo-secondary text-center sm:!text-left !mt-4"></p>
      </div>
      <slot name="decorations" />
    </div>
  </div>
</template>

<script setup>
import "@/src/i18n";
import { reactive, ref, inject, onMounted, defineProps, watch } from "vue";
import { Toggle, Button, ImageButton, Image, MinusIcon } from "prism";
import { useUploaderSamples } from "../../composables/uploader_samples";

const props = defineProps({
  displayWidget: {
    type: Boolean,
    default: true,
  },
  centeredUploadButton: {
    type: Boolean,
    default: true,
  },
  setDefaultPresetValues: {
    type: Boolean,
    default: false,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  showBlurToggle: {
    type: Boolean,
    default: false,
  },
  blurToggleDefault: {
    type: Boolean,
    default: false,
  },
  showShadowToggle: {
    type: Boolean,
    default: false,
  },
  shadowToggleDefault: {
    type: Boolean,
    default: false,
  },
  backgroundColorSamples: {
    type: Array,
    default: [],
  },
  backgroundImageSamples: {
    type: Array,
    default: [],
  },
  backgroundSamplesCollectionIds: {
    type: Array,
    default: [],
  },
  foregroundImageSamples: {
    type: Array,
    default: () => {
      const { availableImages } = useUploaderSamples();
      return availableImages.map((element) => {
        const n = Math.floor(Math.random() * element.length);
        return element[n];
      });
    },
  },
});

const I18n = inject("I18n");
const exampleImages = ref(props.foregroundImageSamples);
const uploadTerms = ref("");
const applyBlur = ref(props.blurToggleDefault);
const applyShadow = ref(props.shadowToggleDefault);
const backgroundImageSelected = ref(null);
const backgroundColorSelected = ref(null);

const widgetElement = ref(null);

const widgetElementStyles = reactive({
  "background": undefined,
});

const isSuggestionImage = (suggestion) => {
  return !!suggestion?.thumbnail_url;
};

const backgroundSuggestions = [...props.backgroundColorSamples, ...props.backgroundImageSamples];

const deselectSuggestion = () => {
  backgroundImageSelected.value = null;
  backgroundColorSelected.value = null;
  widgetElementStyles["background"] = undefined;
};

const selectSuggestion = (suggestion) => {
  // if suggestion is an image
  if (suggestion?.thumbnail_url) {
    return selectImage(suggestion);
  }
  // if suggestion is a color
  return selectColor(suggestion);
};

const selectImage = (image) => {
  deselectSuggestion();
  backgroundImageSelected.value = {
    id: "",
    ...image,
  };
  widgetElementStyles["background"] = `url(${image.url})`;
};

const selectColor = (color) => {
  deselectSuggestion();
  backgroundColorSelected.value = color;

  widgetElementStyles["background"] = `${color} !important`;
};

const isSelected = (suggestion) => {
  // if suggestion is an image
  if (isSuggestionImage(suggestion)) {
    return suggestion.thumbnail_url === backgroundImageSelected.value?.thumbnail_url;
  }
  // if suggestion is a color
  return suggestion === backgroundColorSelected.value;
};

onMounted(() => {
  // TODO Improve this
  const metaElement = document.querySelector("meta[name='upload-page:t']");
  const metaContent = JSON.parse(metaElement.getAttribute("content"));
  uploadTerms.value = metaContent.upload_terms_prism;

  if (props.setDefaultPresetValues) {
    setUploadPresetState();
  }
});

const toggleShadow = () => {
  applyShadow.value = !applyShadow.value;
};

const toggleBlur = () => {
  applyBlur.value = !applyBlur.value;
};

const setUploadPresetState = () => {
  window.setUploadPresetState({
    background: backgroundImageSelected.value,
    backgroundColor: backgroundColorSelected.value,
    shadow: applyShadow.value,
    blur: applyBlur.value,
    backgroundSamplesCollectionIds: props.backgroundSamplesCollectionIds,
    backgroundColorSamples: props.backgroundColorSamples,
  });
};

watch([backgroundColorSelected, backgroundImageSelected, applyShadow, applyBlur], () => {
  setUploadPresetState();
});

const exampleSelected = (image) => {
  window.onExampleSelected(image);
  scrollToTop();
};

const uploadFile = () => {
  window.onUploadFile();
  scrollToTop();
};

const uploadUrl = (url) => {
  window.onUploadUrl(url);
  scrollToTop();
};

const scrollToTop = () => {
  window.scrollTo({ top: 0 });
};
</script>

<style scoped>
.playful-rotate {
  & > :nth-child(even) {
    transform: rotate(3deg);
  }
  & > :nth-child(odd) {
    transform: rotate(-3deg);
  }
}

#background-widget {
  display: grid;
  grid-template-rows: auto;
}

@media (min-width: 640px) {
  .full-screen-height {
    height: 80vh;
    max-height: 1000px;
  }

  /* mimick previous button layout that had sm:pt-36 sm:pb-16*/
  .centered-upload-button {
    min-height: 21.5rem;
    padding-top: 4rem;
  }

  .button-padding {
    padding: 0.5rem;
  }
}

.light-glass-background {
  background-color: #ffffffcc;
  backdrop-filter: blur(1px);
}
</style>
